
import React, { useState, useMemo } from 'react';
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search } from "lucide-react";
import { useIDTypes } from "@/hooks/admin/identity-docs/useIDTypes";

interface DocumentTypeSelectorProps {
  value: string;
  onValueChange: (value: string) => void;
}

export function DocumentTypeSelector({ value, onValueChange }: DocumentTypeSelectorProps) {
  const { idTypes, isLoading: isLoadingTypes } = useIDTypes();
  const [searchTerm, setSearchTerm] = useState("");

  const filteredIdTypes = useMemo(() => {
    if (!searchTerm) return idTypes;
    
    return idTypes.filter(type => 
      type.document_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      type.country_code.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [idTypes, searchTerm]);

  return (
    <div className="grid gap-2">
      <Label htmlFor="documentType">Document Type</Label>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger>
          <SelectValue placeholder="Select document type" />
        </SelectTrigger>
        <SelectContent>
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search document types..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
          <div className="max-h-[200px] overflow-y-auto">
            {isLoadingTypes ? (
              <SelectItem value="loading-placeholder" disabled>Loading...</SelectItem>
            ) : filteredIdTypes.length === 0 ? (
              <SelectItem value="no-results" disabled>
                {searchTerm ? "No document types found" : "No document types available"}
              </SelectItem>
            ) : (
              filteredIdTypes.map(type => (
                <SelectItem key={type.id} value={type.id}>
                  {type.document_name} ({type.country_code})
                </SelectItem>
              ))
            )}
          </div>
        </SelectContent>
      </Select>
    </div>
  );
}
